import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, useToast } from '@/components/ui';
import {
  useRevokeAllStaffSessions,
  useRevokeStaffSession,
  useStaffSessions,
} from './api';
import { RevokeSessionDialog } from './RevokeSessionDialog';
import { SessionList } from './SessionList';
import styles from './StaffSessions.module.css';
import type { StaffSession } from './types';

type PendingAction = { kind: 'one'; session: StaffSession } | { kind: 'all' };

export function StaffSessionsDrawer({
  staffId,
  staffName,
  onClose,
}: {
  staffId: string;
  staffName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const sessions = useStaffSessions(staffId);
  const revokeOne = useRevokeStaffSession(staffId);
  const revokeAll = useRevokeAllStaffSessions(staffId);
  const [action, setAction] = useState<PendingAction | null>(null);
  const liveOtherSessions = (sessions.data ?? []).filter(
    (session) => !session.current && !session.revokedAt,
  );

  function resetAction() {
    revokeOne.reset();
    revokeAll.reset();
    setAction(null);
  }

  async function confirm(reason: string) {
    if (!action) return;
    try {
      const result =
        action.kind === 'all'
          ? await revokeAll.mutateAsync(reason)
          : await revokeOne.mutateAsync({
              sessionId: action.session.id,
              reason,
            });
      toast.show({
        tone: 'success',
        title: t('staffSessions.staffSignedOutToast', {
          count: result.revoked,
        }),
      });
      setAction(null);
    } catch {
      // The confirmation dialog keeps the ApiError and requestId visible.
    }
  }

  return (
    <Dialog
      open
      title={t('staffSessions.staffTitle', { name: staffName })}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className={styles.actions}>
        <p className={styles.intro}>{t('staffSessions.staffIntro')}</p>
        <Button
          size="sm"
          variant="danger"
          disabled={liveOtherSessions.length === 0}
          onClick={() => setAction({ kind: 'all' })}
        >
          {t('staffSessions.signOutEverywhere')}
        </Button>
      </div>
      <SessionList
        sessions={sessions.data}
        loading={sessions.isLoading}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        onRevoke={(session) => setAction({ kind: 'one', session })}
        revokingId={
          revokeOne.isPending && action?.kind === 'one'
            ? action.session.id
            : undefined
        }
      />
      {action && (
        <RevokeSessionDialog
          target={action.kind === 'all' ? staffName : action.session.device}
          all={action.kind === 'all'}
          requiresReason
          pending={revokeOne.isPending || revokeAll.isPending}
          error={revokeOne.error ?? revokeAll.error}
          onClose={resetAction}
          onConfirm={(reason) => void confirm(reason)}
        />
      )}
    </Dialog>
  );
}
